import { Checkbox, CheckboxProps, Form, Label, Radio, Select } from "semantic-ui-react";
import { isNumber } from "lodash";
import moment from "moment";
import { FormError } from "../models/FormError";
import ErrorsView from "./ErrorsView";
import { useEffect, useState } from "react";
import TimePicker from "./common/TimePicker";
import DateTimePicker from "./common/DateTimePicker";
import PermitFieldValuesForm from "./PermitFieldValuesForm";
import { TemplateField } from "../models/TemplateField";
import { PermitFieldValueInput } from "../models/globalTypes";
import { StateKeyList } from "../models/StateKey";
import { PERMIT_DEFAULTS } from "../constants";

export interface FormData {
  number: number;
  comments: string;
  contractor: string;
  subContractor: string;
  locationOfWork: string;
  worksDescription: string;
  from?: Date;
  to?: Date;
  hoursFrom: string;
  hoursTo: string;
  endsNextDay: boolean;
  noAffectedServices: boolean;
  affectedServices: string[];
  asBuiltRequired: boolean;
  fieldValues?: PermitFieldValueInput[];
}

interface Props {
  affectedServicesOptions?: string[];
  formData: FormData;
  formErrors: FormError;
  errors: string[] | undefined;
  onChange: (formData: FormData) => void;
  isNumberEditable: boolean;
  templateFields?: TemplateField[];
  isAffectedServicesEnabled?: boolean;
  isAsBuiltEnabled?: boolean;
  maxPermitDurationDays?: number | null;
  maxPermitValidInAdvanceDays?: number | null;
}

const RequestPermitForm = ({
  affectedServicesOptions,
  formData,
  formErrors,
  errors,
  onChange,
  isNumberEditable,
  templateFields,
  isAffectedServicesEnabled,
  isAsBuiltEnabled,
  maxPermitDurationDays,
  maxPermitValidInAdvanceDays,
}: Props) => {
  const [affectedSrvOptions, setAffectedSrvOptions] = useState<Array<any>>([]);

  useEffect(() => {
    if (affectedServicesOptions) {
      setAffectedSrvOptions(affectedServicesOptions?.map((a) => ({ key: a, text: a, value: a })));
    }
  }, [affectedServicesOptions]);

  const handleAddAffectedSrv = (e: any, { value }: any) => {
    setAffectedSrvOptions([{ text: value, value }, ...affectedSrvOptions]);
  };

  const handleChange = (e: any, { name, value }: any) => {
    onChange({ ...formData, [name]: value });
  };

  const handleasBuiltRequiredChange = (value: boolean) => {
    onChange({ ...formData, asBuiltRequired: value ?? false });
  };

  const handleEndsNextDayChange = (value: boolean) => {
    onChange({ ...formData, endsNextDay: value ?? false });
  };

  const handleNoAffectedServicesChange = (e: any, data: CheckboxProps) => {
    onChange({
      ...formData,
      noAffectedServices: data.value === "true" ? true : false,
      affectedServices: data.value === "true" ? [] : formData.affectedServices,
    });
  };

  const onFromDateChange = (e: any, data: any) => {
    const durationDays = isNumber(maxPermitDurationDays)
      ? Math.min(PERMIT_DEFAULTS.DEFAULT_PERMIT_DURATION - 1, maxPermitDurationDays - 1)
      : PERMIT_DEFAULTS.DEFAULT_PERMIT_DURATION - 1;
    onChange({
      ...formData,
      from: data.value,
      to:
        data.value && !formData.to
          ? new Date(new Date(data.value).setDate(data.value.getDate() + durationDays))
          : formData.to,
    });
  };

  const fromMinDate = moment().startOf("day").toDate();

  const fromMaxDate = isNumber(maxPermitValidInAdvanceDays)
    ? moment().startOf("day").add(maxPermitValidInAdvanceDays, "days").toDate()
    : undefined;

  const fromStartOfDay = formData.from ? moment(formData.from).startOf("day") : undefined;

  const toMinDate = fromStartOfDay?.toDate();

  const toMaxDate =
    fromStartOfDay && isNumber(maxPermitDurationDays)
      ? fromStartOfDay
          .clone()
          .add(maxPermitDurationDays - 1, "days")
          .toDate()
      : undefined;

  return (
    <>
      <ErrorsView errors={errors} />
      <Form>
        {isNumberEditable && (
          <Form.Group widths="2">
            <Form.Field required>
              <label>Permit no.</label>
              <Form.Input
                disabled={!isNumberEditable}
                error={formErrors.number}
                name="number"
                value={formData.number}
                onChange={handleChange}
              />
            </Form.Field>
          </Form.Group>
        )}
        <Form.Group widths="2">
          <Form.Field>
            <label>Contractor</label>
            <Form.Input
              error={formErrors.contractor}
              name="contractor"
              value={formData.contractor}
              onChange={handleChange}
            />
          </Form.Field>
          <Form.Field>
            <label>Sub-contractor</label>
            <Form.Input
              error={formErrors.subContractor}
              name="subContractor"
              value={formData.subContractor}
              onChange={handleChange}
            />
          </Form.Field>
        </Form.Group>
        <Form.Group widths="2">
          <Form.Field required>
            <label>Works description</label>
            <Form.Input
              error={formErrors.worksDescription}
              name="worksDescription"
              value={formData.worksDescription}
              onChange={handleChange}
            />
          </Form.Field>
          <Form.Field required>
            <label>Location of work</label>
            <Form.Input
              error={formErrors.locationOfWork}
              name="locationOfWork"
              value={formData.locationOfWork}
              onChange={handleChange}
            />
          </Form.Field>
        </Form.Group>
        <Form.Group widths="2">
          <DateTimePicker
            label="Valid From"
            value={formData.from}
            onChange={(date) => {
              const data = { value: date };
              onFromDateChange(undefined, data);
            }}
            error={formErrors.from}
            required
            minDate={fromMinDate}
            maxDate={fromMaxDate}
          />
          <DateTimePicker
            label="To"
            value={formData.to}
            onChange={(date) => onChange({ ...formData, to: date })}
            error={formErrors.to}
            required
            minDate={toMinDate}
            maxDate={toMaxDate}
          />
        </Form.Group>
        <Form.Group widths="2">
          <TimePicker
            hoursLabel="Hours from"
            minutesLabel="Minutes from"
            controlName="hoursFrom"
            error={formErrors.hoursFrom}
            timeSpan={formData.hoursFrom}
            onChange={handleChange}
          />
          <Form.Field>
            <div style={{ display: "flex", alignItems: "flex-end", gap: "1em" }}>
              <div style={{ flex: 1 }}>
                <TimePicker
                  hoursLabel="Hours to"
                  minutesLabel="Minutes to"
                  controlName="hoursTo"
                  error={formErrors.hoursTo}
                  timeSpan={formData.hoursTo}
                  onChange={handleChange}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.2em" }}>
                <Checkbox
                  toggle
                  name="endsNextDay"
                  checked={formData.endsNextDay}
                  onChange={(_, data) => handleEndsNextDayChange(data.checked as boolean)}
                />
                <Label basic style={{ margin: 0, padding: 0, border: "none" }}>
                  Ends next day
                </Label>
              </div>
            </div>
          </Form.Field>
        </Form.Group>
        {templateFields && templateFields.length > 0 && (
          <PermitFieldValuesForm
            templateFields={templateFields}
            fieldValues={formData.fieldValues ?? []}
            onChange={(values) => onChange({ ...formData, fieldValues: values })}
            editableStageKey={StateKeyList.REQUEST_IN_PROGRESS.key}
            requiredForStateKey={StateKeyList.REQUESTED.key}
            fieldErrors={formErrors}
          />
        )}
        {isAffectedServicesEnabled && (
          <>
            <Form.Group>
              <Form.Field required>
                <Checkbox
                  radio
                  label="Affected services"
                  name="affectedServicesRadioGroup"
                  value="false"
                  checked={!formData.noAffectedServices}
                  onChange={handleNoAffectedServicesChange}
                />
              </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Field
                disabled={formData.noAffectedServices}
                error={formErrors.affectedServices}
                name="affectedServices"
                control={Select}
                fluid
                multiple
                search
                selection
                allowAdditions
                value={formData.affectedServices}
                options={affectedSrvOptions}
                onAddItem={handleAddAffectedSrv}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Field>
                <Checkbox
                  radio
                  label="I can confirm there are no affected services"
                  name="affectedServicesRadioGroup"
                  value="true"
                  checked={formData.noAffectedServices}
                  onChange={handleNoAffectedServicesChange}
                />
              </Form.Field>
            </Form.Group>
          </>
        )}
        {isAsBuiltEnabled && (
          <Form.Group widths="equal">
            <Form.Field required error={formErrors.asBuiltRequired}>
              <label>As-Built Required</label>
              <div role="radiogroup" style={{ display: "flex", gap: 16 }}>
                <Radio
                  label="Yes"
                  name={"asBuiltRequired"}
                  checked={formData.asBuiltRequired === true}
                  onChange={() => handleasBuiltRequiredChange(true)}
                />
                <Radio
                  label="No"
                  name={"asBuiltRequired"}
                  checked={formData.asBuiltRequired === false}
                  onChange={() => handleasBuiltRequiredChange(false)}
                />
              </div>
            </Form.Field>
          </Form.Group>
        )}
        <Form.Group widths="equal">
          <Form.TextArea
            rows={2}
            label="Comments"
            error={formErrors.comments}
            name="comments"
            value={formData.comments}
            onChange={handleChange}
          />
        </Form.Group>
      </Form>
    </>
  );
};

export default RequestPermitForm;
